import React from 'react';
import { TYPOGRAPHY, COLORS } from '../../styles/UISystem';

/**
 * Typography Components
 * Reusable text style components based on the UI System
 */

export const HeaderLarge = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.HEADER_LARGE, ...style }} {...props}>
    {children}
  </div>
);

export const HeaderMedium = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.HEADER_MEDIUM, ...style }} {...props}>
    {children}
  </div>
);

export const HeaderSmall = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.HEADER_SMALL, ...style }} {...props}>
    {children}
  </div>
);

export const TextLarge = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.TEXT_LARGE, ...style }} {...props}>
    {children}
  </div>
);

export const BodyRegular = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.BODY_REGULAR, ...style }} {...props}>
    {children}
  </div>
);

export const BodyLight = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.BODY_LIGHT, ...style }} {...props}>
    {children}
  </div>
);

export const BodySmall = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.BODY_SMALL, ...style }} {...props}>
    {children}
  </div>
);

export const ButtonRegular = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.BUTTON_REGULAR, ...style }} {...props}>
    {children}
  </div>
);

export const ButtonLarge = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.BUTTON_LARGE, ...style }} {...props}>
    {children}
  </div>
);

export const ButtonDisabled = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.BUTTON_DISABLED, ...style }} {...props}>
    {children}
  </div>
);

export const CaptionText = ({ children, style, ...props }) => (
  <div style={{ ...TYPOGRAPHY.CAPTION_TEXT, ...style }} {...props}>
    {children}
  </div>
);

/**
 * Typography Example Component
 * Used for displaying typography examples in the UI System
 */
export const TypographyExample = ({ name, style, description, usage }) => {
  // Determine if this style needs a dark background (for light text)
  const needsDarkBackground = 
    name.includes('BUTTON_') && 
    style.color === COLORS.LIGHT_GRAY;
  
  // Get human-readable name from token name (e.g., HEADER_MEDIUM -> Header Medium)
  const readableName = name
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
  
  return (
    <div style={{ marginBottom: '24px' }}>
      <div 
        style={{ 
          ...style, 
          marginBottom: '8px',
          padding: '8px',
          backgroundColor: needsDarkBackground ? COLORS.DARK_GRAY : 'transparent',
          borderRadius: '4px'
        }}
      >
        {readableName}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', color: COLORS.DARK_GRAY }}>
          {name}
        </div>
        <div style={{ fontSize: '14px', color: COLORS.DARK_GRAY }}>
          {description}
        </div>
        <div style={{ fontSize: '14px', color: COLORS.DARK_GRAY }}>
          Used for: {usage}
        </div>
      </div>
    </div>
  );
};

// Export all typography styles with their descriptions
export const TEXT_STYLES = [
  {
    name: 'HEADER_LARGE',
    style: TYPOGRAPHY.HEADER_LARGE,
    description: '24px, Bold, DarkGray text',
    usage: 'Page titles'
  },
  {
    name: 'HEADER_MEDIUM',
    style: TYPOGRAPHY.HEADER_MEDIUM,
    description: '20px, Bold, DarkGray text',
    usage: 'Section headers'
  },
  {
    name: 'HEADER_SMALL',
    style: TYPOGRAPHY.HEADER_SMALL,
    description: '16px, Bold, DarkGray text',
    usage: 'Component headers'
  },
  {
    name: 'TEXT_LARGE',
    style: TYPOGRAPHY.TEXT_LARGE,
    description: '24px, Regular, DarkGray text',
    usage: 'Overlays, Larger text'
  },
  {
    name: 'BODY_REGULAR',
    style: TYPOGRAPHY.BODY_REGULAR,
    description: '16px, Regular, DarkGray text',
    usage: 'Primary content'
  },
  {
    name: 'BODY_LIGHT',
    style: TYPOGRAPHY.BODY_LIGHT,
    description: '16px, Regular, MediumGray text',
    usage: 'Placeholder content'
  },
  {
    name: 'BODY_SMALL',
    style: TYPOGRAPHY.BODY_SMALL,
    description: '14px, Regular, DarkGray text',
    usage: 'Profile labels in headers'
  },
  {
    name: 'BUTTON_REGULAR',
    style: TYPOGRAPHY.BUTTON_REGULAR,
    description: '16px, Regular, LightGray text',
    usage: 'Light text on dark background'
  },
  {
    name: 'BUTTON_LARGE',
    style: TYPOGRAPHY.BUTTON_LARGE,
    description: '24px, Regular, LightGray text',
    usage: 'Larger light text on dark buttons'
  },
  {
    name: 'BUTTON_DISABLED',
    style: TYPOGRAPHY.BUTTON_DISABLED,
    description: '16px, Light, LightGray text',
    usage: 'Extra light text on dark background (disabled)'
  },
  {
    name: 'CAPTION_TEXT',
    style: TYPOGRAPHY.CAPTION_TEXT,
    description: '12px, Regular, DarkGray text',
    usage: 'Supporting text, timestamps'
  }
];
